import * as React from 'react';
import { FC, useState } from 'react';

import { useIntl } from 'react-intl';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import Animation from 'components/lottie/favorite/loading';
import { SaveFavoriteOrder } from 'components/save-favorite-order';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryInput,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavoritesRefsQuery,
} from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { isOfferType } from 'state/order/utils';
import { frontendToBackendCartEntryMap } from 'utils/cart';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { Wrapper } from './favorite-order-button.styled';
import { IFavoriteOrderButtonProps } from './types';

/**
 *
 * Favorite Order Button
 *
 */
const FavoriteOrderButton: FC<IFavoriteOrderButtonProps> = ({ order }) => {
  const { formatMessage } = useIntl();
  const { rbiOrderId } = order;
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
  const mParticle = useMParticleContext();

  const shouldDisplayHeart = !(order.cart.cartEntries || []).some((entry: IBackendCartEntries) =>
    isOfferType(entry.type)
  );

  const { data } = useGetUserFavoritesRefsQuery({
    skip: !shouldDisplayHeart,
  });

  const [deleteFavorite, { loading: deleteLoading }] = useDeleteFavoriteMutation({
    onCompleted: () => toast.success(formatMessage({ id: 'removeFavoriteSucess' })),
  });
  const [createFavorite, { loading: createLoading }] = useCreateFavoriteMutation({
    onCompleted: () => toast.success(formatMessage({ id: 'saveFavoriteOrderSucess' })),
  });

  const onClick = () => {
    if (isFavorite) {
      openRemoveFavoriteModal();
    } else {
      setSaveModalOpen(true);
    }
  };

  const entries = order.cart.cartEntries.map(cartEntry =>
    frontendToBackendCartEntryMap(mapBackendToFrontend(cartEntry))
  ) as unknown as ICartEntryInput[];

  const saveFavoriteOrder = async (name: string) => {
    return await createFavorite({
      variables: {
        input: {
          entries,
          name,
          ref: rbiOrderId,
        },
      },
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const removeFavoriteOrder = async (favoriteId: string) => {
    return await deleteFavorite({
      variables: {
        favoriteId,
      },
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const refFavorite = data?.userFavorites.favorites?.find(favorite => favorite?.ref === rbiOrderId);
  const isFavorite = !!refFavorite;

  const [RemoveFavoriteModal, openRemoveFavoriteModal] = useDialogModal({
    onConfirm: () => removeFavoriteOrder(refFavorite?._id ?? ''),
    showCancel: true,
    modalAppearanceEventMessage: 'Delete Favorite Order',
  });

  const loading = createLoading || deleteLoading;

  const FavIcon = <FavoriteIcon isFavorite={isFavorite} />;

  return shouldDisplayHeart ? (
    <>
      <Wrapper>
        <ButtonForIconFav
          data-testid={isFavorite ? 'fav-icon-selected' : 'fav-icon'}
          onClick={onClick}
          aria-label={formatMessage({
            id: isFavorite ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
          })}
          isTransparent={true}
        >
          {loading ? (
            <React.Suspense data-testid="fav_order_loading_animation" fallback={FavIcon}>
              <Animation />
            </React.Suspense>
          ) : (
            FavIcon
          )}
        </ButtonForIconFav>
      </Wrapper>

      <RemoveFavoriteModal
        buttonLabel={formatMessage({ id: 'remove' })}
        heading={formatMessage({ id: 'removeFavorite' })}
        body={formatMessage({ id: 'removeFavoriteConfirm' })}
        showCloseButton={true}
      />
      {saveModalOpen && (
        <SaveFavoriteOrder
          onSave={name => {
            saveFavoriteOrder(name).then(() => {
              mParticle.logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.UserContent, {
                Name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
                'Source Page': `${routes.menu}/`,
              });
            });
            setSaveModalOpen(false);
          }}
          onDismiss={() => setSaveModalOpen(false)}
        />
      )}
    </>
  ) : null;
};

export default FavoriteOrderButton;
